'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { SITE } from '@/app/constants';

interface BentoCopy {
  title: string;
  description: string;
}

/** 右高栏 Agent 产品展示图 */
const AGENT_IMAGE_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/viduagent-162876d6.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/viduagent-162876d6.webp';

const toolsCardsImageUrl =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/tools-cards-a61ca965.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/tools-cards-a61ca965.webp';

const pricingImageUrl =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/pricing-ultimate-500834d9.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/pricing-ultimate-500834d9.webp';

const appImageUrl =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/app-vidu-pro-440c4dfd.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/app-vidu-pro-440c4dfd.webp';

/** 每格的双层圆角外壳：外圈底色 + 内圈 +1px，保证内层媒体不压住描边。 */
const CELL_SHELL = 'absolute inset-px rounded-[2rem] bg-system-white06 sm:rounded-16 md:rounded-16';
const CELL_INNER =
  'relative flex h-full flex-col overflow-hidden rounded-[calc(2rem+1px)] sm:rounded-[17px] md:rounded-[17px]';
const CELL_RING =
  'pointer-events-none absolute inset-px rounded-[2rem] shadow-sm outline outline-1 outline-white/15 sm:rounded-16 md:rounded-16';

/**
 * 节奏感进场：四格按「哒·哒·哒——哒」的拍子上浮，前三拍紧凑、第四拍留白后落下，
 * 而不是匀速依次平移。数值是拍点（秒）。
 */
const BEATS = [0, 0.2, 0.4, 0.72] as const;

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const headerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (beat: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: beat, duration: 1.1, ease: EASE_OUT },
  }),
};

/** 卡片进场：上浮 + 轻微放大回正，落点用 spring 收尾制造「落下」的手感。 */
const cellVariants = {
  hidden: { opacity: 0, y: 48, scale: 0.97 },
  visible: (beat: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      opacity: { delay: beat, duration: 0.9, ease: EASE_OUT },
      y: { delay: beat, type: 'spring' as const, stiffness: 55, damping: 18, mass: 1.1 },
      scale: { delay: beat, duration: 1.2, ease: EASE_OUT },
    },
  }),
};

/** 卡片内媒体：比卡片本身晚半拍浮现，形成层次。 */
const mediaVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (beat: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: beat + 0.28, duration: 1, ease: EASE_OUT },
  }),
};

/** 单元格文案：标题 + 一句描述，窄屏居中。 */
const CellCopy = ({ copy }: { copy: BentoCopy }) => (
  <>
    <p className="mt-2 font-medium text-[22px] text-white tracking-tight sm:text-center sm:text-[18px] md:text-center">
      {copy.title}
    </p>
    <p className="mt-2 max-w-lg text-[14px] text-white/60 leading-6 sm:text-center sm:text-[13px] sm:leading-5 md:text-center">
      {copy.description}
    </p>
  </>
);

/** 一格 bento：外层负责网格定位与进场节奏，内层是双层圆角外壳。 */
const BentoCell = ({
  className,
  beat,
  copy,
  copyClassName,
  children,
}: {
  className: string;
  beat: number;
  copy: BentoCopy;
  copyClassName: string;
  children: ReactNode;
}) => (
  <motion.div className={className} variants={cellVariants} custom={beat}>
    <div className={CELL_SHELL} />
    <div className={CELL_INNER}>
      <div className={copyClassName}>
        <CellCopy copy={copy} />
      </div>
      <motion.div className="flex min-h-0 flex-1 flex-col" variants={mediaVariants} custom={beat}>
        {children}
      </motion.div>
    </div>
    <div className={CELL_RING} />
  </motion.div>
);

/**
 * 第 4 屏：bento 网格能力展示。
 *
 * 3 列 2 行：左右两格各占满一列（跨 2 行），中列上下各一格；四格均显式定位，
 * 否则自动流会把中列下格排到第 3 列。窄屏（md，≤1023px）退化为单列堆叠。
 *
 * 进场按 BEATS 的拍点错峰上浮（前三拍紧凑、第四拍留白后落下），媒体再晚半拍浮现；
 * prefers-reduced-motion 时全部静态渲染。
 *
 * 四格文案统一取 home3.bento（Vidu APP / 套餐价格 / 创作工具 / Agent产品），
 * 前 3 格配图用 public/images/bento 下的静态图，第 4 格是 Agent 产品展示图。
 */
export const TabShowcaseScreen = () => {
  const tBento = useTranslations('home3.bento');
  const prefersReducedMotion = useReducedMotion();

  const copyOf = (key: string): BentoCopy => ({
    title: tBento(`${key}.title`),
    description: tBento(`${key}.description`),
  });

  const app = copyOf('app');
  const pricing = copyOf('pricing');
  const tools = copyOf('tools');
  const agent = copyOf('agent');

  // reduced-motion：不做位移/淡入，直接静态呈现
  const motionProps = prefersReducedMotion
    ? {}
    : ({
        initial: 'hidden',
        whileInView: 'visible',
        // H5 单列后容器远高于视口，比例阈值可能永远达不到；只要有内容进入就触发。
        viewport: { once: true, amount: 'some' },
      } as const);

  return (
    <div className="w-full bg-black py-24 sm:py-12 md:py-12">
      <motion.div className="mx-auto w-full max-w-[1600px] px-10 sm:px-4 md:px-4" {...motionProps}>
        <motion.h2
          className="mx-auto max-w-[46rem] text-center font-semibold text-[3.5rem] text-white leading-[1.05] tracking-tight [text-wrap:balance] sm:text-[2rem] sm:leading-[1.12] md:text-[2.5rem] lg:text-[3rem]"
          variants={headerVariants}
          custom={0}
        >
          {tBento('title')}
        </motion.h2>
        {/* 原本在标题上方的小标题，改到标题下方：白色 16px、64% 透明度、不加粗 */}
        <motion.p
          className="mt-4 text-center font-normal text-[16px] text-system-white64 sm:mt-3 sm:text-[14px] md:mt-3"
          variants={headerVariants}
          custom={0.08}
        >
          {tBento('eyebrow')}
        </motion.p>

        <div className="mt-16 grid h-[760px] grid-cols-3 grid-rows-2 gap-4 sm:mt-8 sm:h-auto sm:grid-cols-1 sm:grid-rows-none sm:gap-3 md:mt-10 md:h-auto md:grid-cols-1 md:grid-rows-none">
          {/* 左高栏：手机取景框 */}
          <BentoCell
            className="relative col-start-1 row-span-2 row-start-1 md:col-start-auto md:row-span-1 md:row-start-auto"
            beat={BEATS[0]}
            copy={app}
            copyClassName="px-8 pt-8 pb-3 sm:px-5 sm:pt-5 sm:pb-0 md:px-6 md:pt-6 md:pb-0"
          >
            <div className="relative min-h-[30rem] w-full grow sm:mx-auto sm:min-h-[20rem] sm:max-w-sm md:mx-auto md:min-h-[24rem] md:max-w-sm">
              <div className="absolute inset-x-10 top-10 bottom-0 overflow-hidden rounded-t-[3rem] border-system-white12 border-x-[12px] border-t-[12px] bg-black sm:inset-x-5 sm:top-6 sm:rounded-t-[2rem] sm:border-x-[8px] sm:border-t-[8px] md:inset-x-6">
                <img
                  src={appImageUrl}
                  alt={app.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-left-top"
                />
              </div>
            </div>
          </BentoCell>

          {/* 中列上格：速度条 */}
          <BentoCell
            className="relative col-start-2 row-start-1 md:col-start-auto md:row-start-auto"
            beat={BEATS[1]}
            copy={pricing}
            copyClassName="px-8 pt-8 sm:px-5 sm:pt-5 md:px-6 md:pt-6"
          >
            <div className="flex flex-1 items-center justify-center px-8 pb-2 sm:px-5 sm:py-6 md:px-6 md:pt-8 md:pb-8">
              <img
                src={pricingImageUrl}
                alt={pricing.title}
                loading="lazy"
                decoding="async"
                className="w-full md:max-w-xs"
              />
            </div>
          </BentoCell>

          {/* 中列下格：安全图示 */}
          <BentoCell
            className="relative col-start-2 row-start-2 md:col-start-auto md:row-start-auto"
            beat={BEATS[2]}
            copy={tools}
            copyClassName="px-8 pt-8 sm:px-5 sm:pt-5 md:px-6 md:pt-6"
          >
            <div className="flex flex-1 items-center justify-center px-8 pb-2 sm:px-5 sm:py-6 md:px-6 md:py-6">
              <img
                src={toolsCardsImageUrl}
                alt={tools.title}
                loading="lazy"
                decoding="async"
                className="max-h-[152px] w-full object-contain md:max-h-[120px]"
              />
            </div>
          </BentoCell>

          {/* 右高栏：Agent 产品展示图 */}
          <BentoCell
            className="relative col-start-3 row-span-2 row-start-1 md:col-start-auto md:row-span-1 md:row-start-auto"
            beat={BEATS[3]}
            copy={agent}
            copyClassName="px-8 pt-8 pb-3 sm:px-5 sm:pt-5 sm:pb-0 md:px-6 md:pt-6 md:pb-0"
          >
            <div className="relative min-h-[30rem] w-full grow sm:min-h-[20rem] md:min-h-[22rem]">
              <div className="absolute top-10 right-0 bottom-0 left-10 overflow-hidden rounded-tl-16 rounded-bl-16 outline outline-1 outline-white/10 sm:top-6 sm:left-5 md:left-6">
                <img
                  src={AGENT_IMAGE_URL}
                  alt={agent.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-left-top"
                />
              </div>
            </div>
          </BentoCell>
        </div>
      </motion.div>
    </div>
  );
};
